import React, { useState, useEffect, useContext } from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { IconButton, MenuList, ListItemText, Divider, Tooltip } from '@mui/material';
import { NotificationsOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import ProfileMenu from './ProfileMenu';
import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import { useSelector } from 'react-redux';
import getUserDetails from '../firebase/authService';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../assets/wte_logo.png';
import { ColorModeContext } from '../App';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

const AppBarComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const isMobileMenuOpen = Boolean(mobileAnchorEl);
  const isNotifMenuOpen = Boolean(notifAnchorEl);

  useEffect(() => {
    getUserDetails();
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleMenuClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = (event) => setMobileAnchorEl(event.currentTarget);
  const handleMobileMenuClose = () => setMobileAnchorEl(null);

  const handleOpenNotifications = (e) => setNotifAnchorEl(e.currentTarget);
  const handleCloseNotifications = () => setNotifAnchorEl(null);
  

  const shouldShowSearchAndIcon = !['/login', '/signup'].includes(location.pathname);

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const toTitleCase = (s) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const labelMap = { 'individual-sites': 'Sites' };
  const formatLabel = (seg) => labelMap[seg] || toTitleCase(seg);
  const breadcrumbs = pathSegments.map((seg, idx) => ({
    label: formatLabel(seg),
    path: '/' + pathSegments.slice(0, idx + 1).join('/'),
  }));
  const pageTitle = breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].label : 'Dashboard';

  const colorMode = useContext(ColorModeContext);
  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'transparent',
          color: (theme) => theme.palette.text.primary,
          boxShadow: 'none',
          borderBottom: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0, 0, 0, 0.12)',
          paddingY: '4px',
          transition: 'all 0.2s ease',
          minHeight: '64px',
          backdropFilter: 'blur(8px)',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.96)',
          left: 0,
          right: 0,
          width: '100%',
          borderRadius: 0,
          '&.MuiAppBar-root': {
            transition: 'all 0.2s ease',
            borderRadius: 0,
          }
        }}
      >
        <Toolbar sx={{ 
          minHeight: '64px !important',
          padding: { xs: '0 8px', sm: '0 16px' },
          marginLeft: 0,
          transition: 'margin-left 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          '& > *': {
            flexShrink: 0,
          },
          '@media (min-width: 900px) and (max-width: 1000px)': {
            padding: '0 8px',
            '& .MuiChip-root': {
              marginRight: '4px',
            },
            '& .MuiIconButton-root': {
              padding: '4px',
            }
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            minWidth: 0,
            flex: '1 1 auto',
            gap: 2,
            '@media (min-width: 900px) and (max-width: 1000px)': {
              maxWidth: '40%'
            }
          }}>
            <Link
              to="/dashboard"
              aria-label="Go to Dashboard"
              title="Dashboard"
              style={{ display: 'inline-flex', lineHeight: 0, textDecoration: 'none' }}
            >
              <img
                src={logo}
                alt="WTE — Dashboard"
                style={{ width: 32, height: 32, borderRadius: 8, cursor: 'pointer' }}
              />
            </Link>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                {breadcrumbs.length === 0 ? 'Home' : (
                  breadcrumbs.slice(0, -1).map((b, i) => (
                    <React.Fragment key={b.path}>
                      <Link to={b.path} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.9 }}>{b.label}</Link>
                      <span style={{ margin: '0 8px', opacity: 0.6 }}>/</span>
                    </React.Fragment>
                  ))
                )}
              </Typography>
              <Typography variant="h6" noWrap sx={{
                color: (theme) => theme.palette.text.primary,
                fontWeight: 600,
                letterSpacing: '0.2px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {pageTitle}
              </Typography>
            </Box>
          </Box>
          
          {/* Desktop Controls */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center', 
            gap: 1,
            flexShrink: 0,
            '@media (min-width: 900px) and (max-width: 1000px)': {
              gap: 0.5
            }
          }}>
            {currentUser && shouldShowSearchAndIcon && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: 1,
                '@media (min-width: 900px) and (max-width: 1000px)': {
                  gap: 0.5
                }
              }}>
                {(() => {
                  const role = (currentUser.role || 'User');
                  const chipPalette = {
                    Admin: (theme) => ({
                      bg: theme.palette.mode === 'dark' ? 'rgba(230,185,5,0.18)' : 'rgba(230,185,5,0.10)',
                      br: 'rgba(230,185,5,0.32)',
                      color: theme.palette.brand.gold,
                    }),
                    Manager: (theme) => ({
                      bg: theme.palette.mode === 'dark' ? 'rgba(91,141,239,0.18)' : 'rgba(91,141,239,0.10)',
                      br: 'rgba(91,141,239,0.30)',
                      color: theme.palette.brand.iris,
                    }),
                    Engineer: (theme) => ({
                      bg: theme.palette.mode === 'dark' ? 'rgba(20,184,166,0.18)' : 'rgba(20,184,166,0.10)',
                      br: 'rgba(20,184,166,0.30)',
                      color: theme.palette.accents.teal,
                    }),
                    User: (theme) => ({
                      bg: theme.palette.mode === 'dark' ? 'rgba(100,116,139,0.18)' : 'rgba(100,116,139,0.10)',
                      br: 'rgba(100,116,139,0.30)',
                      color: theme.palette.accents.slate,
                    }),
                  };
                  const getStyles = (theme) => (chipPalette[role] ? chipPalette[role](theme) : chipPalette['User'](theme));
                  return (
                    <Chip
                      label={`${role}`}
                      size="small"
                      sx={{
                        backgroundColor: (theme) => getStyles(theme).bg,
                        color: (theme) => getStyles(theme).color,
                        border: (theme) => `1px solid ${getStyles(theme).br}`,
                        height: '28px',
                        '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                      }}
                    />
                  );
                })()}
              </Box>
            )}
            
            {shouldShowSearchAndIcon && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1,
                flexWrap: 'nowrap',
                '@media (min-width: 900px) and (max-width: 1000px)': {
                  gap: 0.5
                }
              }}>
                <Search sx={{ 
                  display: { xs: 'none', sm: 'block' },
                  width: { sm: 'auto', md: '200px' },
                  '@media (min-width: 900px) and (max-width: 1000px)': {
                    width: '150px'
                  }
                }}>
                  <SearchIconWrapper>
                    <SearchIcon sx={{ color: (theme) => theme.palette.text.secondary, fontSize: '20px' }} />
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder="Search…"
                    inputProps={{ 'aria-label': 'search' }}
                    sx={{
                      color: (theme) => theme.palette.text.primary,
                      '& .MuiInputBase-input::placeholder': {
                        color: (theme) => theme.palette.text.secondary,
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 8px 8px 40px',
                        fontSize: '0.875rem',
                      }
                    }}
                  />
                </Search>

                {/* Site Switcher removed per request */}

                <Tooltip title={colorMode.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <IconButton 
                    onClick={colorMode.toggleColorMode}
                    aria-label="Toggle theme"
                    sx={{ 
                      color: (theme) => theme.palette.text.primary,
                      '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: (theme) => theme.palette.brand.royalBlue },
                      '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand.royalBlue}`, outlineOffset: 2, borderRadius: '8px' }
                    }}
                  >
                    {colorMode.mode === 'dark' ? <Brightness7Icon sx={{ fontSize: '22px' }} /> : <Brightness4Icon sx={{ fontSize: '22px' }} />}
                  </IconButton>
                </Tooltip>

                <IconButton 
                  onClick={handleOpenNotifications} 
                  aria-label="Open notifications menu"
                  sx={{ 
                    color: (theme) => theme.palette.text.primary,
                    '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: (theme) => theme.palette.brand.royalBlue },
                    '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand.royalBlue}`, outlineOffset: 2, borderRadius: '8px' }
                  }}
                >
                  <NotificationsOutlined sx={{ fontSize: '22px' }} />
                </IconButton>

                <IconButton 
                  onClick={handleMenuOpen} 
                  aria-label="Open profile menu"
                  sx={{ 
                    color: (theme) => theme.palette.text.primary,
                    '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: (theme) => theme.palette.brand.royalBlue },
                    '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.brand.royalBlue}`, outlineOffset: 2, borderRadius: '8px' }
                  }}
                >
                  <PersonOutlineOutlinedIcon sx={{ fontSize: '22px' }} />
                </IconButton>

                <ProfileMenu 
                  anchorEl={anchorEl} 
                  open={open} 
                  onClose={handleMenuClose} 
                />
              </Box>
            )}
          </Box>
          {/* Mobile Menu Icon */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={colorMode.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton 
                onClick={colorMode.toggleColorMode}
                aria-label="Toggle theme"
                sx={{ 
                  color: (theme) => theme.palette.text.primary,
                  '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: '#0A84FF' },
                  '&:focus-visible': { outline: '2px solid #0A84FF', outlineOffset: 2, borderRadius: '8px' }
                }}
              >
                {colorMode.mode === 'dark' ? <Brightness7Icon sx={{ fontSize: '22px' }} /> : <Brightness4Icon sx={{ fontSize: '22px' }} />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleMobileMenuOpen} color="inherit" aria-label="Open menu" sx={{ '&:focus-visible': { outline: '2px solid #0A84FF', outlineOffset: 2, borderRadius: '8px' } }}>
              <MenuIcon />
            </IconButton>
          </Box>
          {/* Mobile Dropdown Menu */}
          <Menu
            anchorEl={mobileAnchorEl}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                borderRadius: '12px',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => theme.palette.background.paper,
                boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 24px rgba(0,0,0,0.10)'
              }
            }}
          >
            {currentUser && shouldShowSearchAndIcon && (
              <MenuItem disabled>{currentUser.role || 'Loading...'}</MenuItem>
            )}
            {currentUser && shouldShowSearchAndIcon && currentUser.role === 'Admin' && (
              <MenuItem onClick={() => { navigate('/user-access'); handleMobileMenuClose(); }}>
                User Access
              </MenuItem>
            )}
            {currentUser && shouldShowSearchAndIcon && currentUser.role === 'Admin' && (
              <MenuItem onClick={() => { navigate('/admin-logs'); handleMobileMenuClose(); }}>
                System Logs
              </MenuItem>
            )}
            {shouldShowSearchAndIcon && (
              <MenuItem>
                <Search sx={{ width: '100%' }}>
                  <SearchIconWrapper>
                    <SearchIcon sx={{ color: '#4E5D78', fontSize: '20px' }} />
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder="Search…"
                    inputProps={{ 'aria-label': 'search' }}
                    sx={{ width: '100%' }}
                  />
                </Search>
              </MenuItem>
            )}
            {shouldShowSearchAndIcon && (
              <MenuItem onClick={(e) => { handleOpenNotifications(e); handleMobileMenuClose(); }}>
                Notifications
              </MenuItem>
            )}
            {shouldShowSearchAndIcon && (
              <MenuItem onClick={(e) => { handleMenuOpen(e); handleMobileMenuClose(); }}>
                Profile
              </MenuItem>
            )}
          </Menu>
        </Toolbar>

        {/* Site Switcher menu removed per request */}

        {/* Notifications Menu */}
        <Menu
          anchorEl={notifAnchorEl}
          open={isNotifMenuOpen}
          onClose={handleCloseNotifications}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ 
            sx: { 
              minWidth: 280,
              borderRadius: '12px',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 24px rgba(0,0,0,0.10)'
            } 
          }}
        >
          <MenuList dense>
            <MenuItem disabled><ListItemText primary="Notifications" secondary="All / Alarms / Tasks" /></MenuItem>
            <Divider />
            <MenuItem onClick={handleCloseNotifications}><ListItemText primary="No new notifications" /></MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleCloseNotifications(); navigate('/notifications'); }}>
              <ListItemText primary="View all notifications" />
            </MenuItem>
          </MenuList>
        </Menu>
      </AppBar>
    </Box>
  );
};

export default AppBarComponent;
